import moment from "moment-timezone";
import { DEFAULT_TZ, SAMPLING_VALUES } from "@/constants";
import { type SamplingValues } from "@/types";
window.moment = moment;

export const localMoment = moment;
export const DDMMYYYYYHHmmSlash = "DD/MM/YYYY HH:mm";
export const YYYYMMDDHHmmss = "YYYY-MM-DD HH:mm:ss";
export const DDMMYYYYHHmmTiret = "DD-MM-YYYY HH:mm";
export const DDMMYYYY = "DD/MM/YYYY";

export const timezone = DEFAULT_TZ;
export const TODAY = new Date().toUTCString();

export const DATE_DAY = [
  moment().startOf("day").subtract(1, "day").toDate().toUTCString(),
  TODAY,
];
export const DATE_3_DAYS = [
  moment().startOf("day").subtract(3, "day").toDate().toUTCString(),
  TODAY,
];
export const DATE_WEEK = [
  moment().startOf("day").subtract(1, "week").toDate().toUTCString(),
  TODAY,
];

export const DATE_2_WEEKS = [
  moment().startOf("day").subtract(2, "week").toDate().toUTCString(),
  TODAY,
];

export const DATE_MONTH = [
  moment().startOf("day").subtract(1, "month").toDate().toUTCString(),
  TODAY,
];

export const DATE_3_MONTHS = [
  moment().startOf("day").subtract(3, "months").toDate().toUTCString(),
  TODAY,
];

export const DATE_6_MONTHS = [
  moment().startOf("day").subtract(6, "months").toDate().toUTCString(),
  TODAY,
];

export const DEFAULT_DATE = DATE_2_WEEKS;

export function dateDifference(begin: string, end: string = null): string {
  const endDate = end ?? new Date();
  let deff = (new Date(endDate).getTime() - new Date(begin).getTime()) / 1000;

  const days = Math.floor(deff / (24 * 60 * 60));
  let hours = 0;
  let minutes = 0;
  deff = deff % (24 * 60 * 60);
  if (deff > 0) {
    hours = Math.floor(deff / (60 * 60));
    deff = deff % (60 * 60);
    if (deff > 0) {
      minutes = Math.round(deff / 60);
    }
  }
  return `${days !== 0 ? days + " d " : ""}${hours !== 0 ? hours + " h " : ""}${
    minutes !== 0 ? minutes + " min " : ""
  }`;
}

export function dateFormatterGenerator(
  date: string | undefined,
  tz?: string,
  format?: string,
): string {
  return date
    ? moment(date)
        .tz(tz || timezone)
        .format(format || DDMMYYYYYHHmmSlash)
    : "-";
}

export function isGoodSampling(
  sampling: number,
  samplingUnit: string,
  period: string[],
): boolean {
  const sampling_value = SAMPLING_VALUES.find(
    (value: SamplingValues) => value.type === samplingUnit,
  );
  return (
    (new Date(period[1]).getTime() - new Date(period[0]).getTime()) /
      ((sampling_value as SamplingValues).value * sampling * 1000) <
    10000
  );
}

export function eventPeriode(periodeEvent: string[]): string[] {
  let newFirstDate: Date = new Date();
  let newLastDate: Date = new Date();
  if (periodeEvent[1]) {
    newLastDate = new Date(periodeEvent[1]);
    newFirstDate = new Date(periodeEvent[0]);
    const dateDiffToDay: number =
      (new Date().getTime() - newLastDate.getTime()) / (1000 * 60 * 60 * 24);
    const dateDiff: number =
      (newLastDate.getTime() - newFirstDate.getTime()) / (1000 * 60 * 60 * 24);
    if (dateDiff + 1 > dateDiffToDay) {
      newLastDate = new Date();
    } else {
      newLastDate.setDate(newLastDate.getDate() + dateDiff + 1);
    }
    newFirstDate.setDate(newFirstDate.getDate() - dateDiff);
  } else {
    newLastDate = new Date();
    newFirstDate = new Date(periodeEvent[0]);
    const dateDiff: number =
      (newLastDate.getTime() - newFirstDate.getTime()) / (1000 * 60 * 60 * 24);
    newFirstDate.setDate(newFirstDate.getDate() - dateDiff);
  }
  return [newFirstDate.toUTCString(), newLastDate.toUTCString()];
}

export function getSamplingFromFrequecy(checkFrequency: number): any {
  const samplingUnit = SAMPLING_VALUES.filter((sampValue: SamplingValues) => {
    return checkFrequency % sampValue.value === 0;
  }).reduce((prev, current) => (prev.value > current.value ? prev : current));
  return {
    sampling: checkFrequency / samplingUnit.value,
    samplingUnit: samplingUnit.type,
  };
}
export function toDaysMinutesSeconds(
  totalSeconds: number,
  onlyDH = false,
  onlyD = false,
): string {
  if (totalSeconds <= 1) {
    return `${totalSeconds}`;
  }

  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const days = Math.floor(totalSeconds / (3600 * 24));

  const secondsStr = makeHumanReadable(seconds, "s");
  const minutesStr = makeHumanReadable(minutes, "min");
  const hoursStr = makeHumanReadable(hours, "h");
  const daysStr = makeHumanReadable(days, "J");

  let result = "";

  if (totalSeconds !== 0) {
    if (onlyDH) {
      if (onlyD) {
        result = `${daysStr}`;
      } else {
        result = `${daysStr} ${hoursStr}`;
      }
    } else {
      result = `${daysStr}${hoursStr}${minutesStr}${secondsStr}`;
    }
  } else {
    result = "0s";
  }

  return result;
}

function makeHumanReadable(num: number, singular: string) {
  return num > 0 ? `${num}${singular}` : "";
}
export function compareSamplingValues(
  value1: string,
  value2: string,
): boolean | null {
  const labelValue1 = SAMPLING_VALUES.find(
    (value: SamplingValues) => value.type === value1,
  )?.value;
  const labelValue2 = SAMPLING_VALUES.find(
    (value: SamplingValues) => value.type === value2,
  )?.value;
  return labelValue1 && labelValue2 ? labelValue1 > labelValue2 : null;
}

export function timeStampToDate(timeStamp: number): any {
  return moment(new Date(timeStamp)).tz(timezone).format("DD/MM/YYYY");
}

export function datePickerStringtoDDMMYYYYFormat(date: any): any {
  return moment(new Date(date)).tz(timezone).format("DD/MM/YYYY");
}

export const areDatesEqual = (dateOne: any, dateTwo: any) => {
  const date1: Date = new Date(dateOne);
  const date2: Date = new Date(dateTwo);
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const calculateDaysFromNow = (dateString: string): number => {
  const startDate = new Date(dateString);
  const currentDate = new Date();

  // Calculate the difference in time
  const diffTime = Math.abs(currentDate.getTime() - startDate.getTime());

  // Convert the difference in time to days
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const dateToUtc = (date: string): any => {
  return moment(date).format("YYYY-MM-DDTHH:mm:ssZ");
};
